import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  overviewItemTitleStyles,
  overviewItemValueStyles,
  overviewSectionContainerStyles,
  overviewSectionTitleStyles,
} from '../../styles/ts/overviewStyles.ts';
import { IAddress } from '../../interfaces';
import AleaEditButton from '../Buttons/AleaEditButton/AleaEditButton.tsx';

interface AddressSectionProps {
  title: string;
  name: string;
  address: IAddress;
  canEdit?: boolean;
  onEdit?: () => void;
  nameLabel?: string;
  editButtonTestId?: string;
}

const AddressSection: React.FC<AddressSectionProps> = ({
  title,
  name,
  address,
  canEdit = false,
  onEdit,
  nameLabel,
  editButtonTestId,
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={overviewSectionContainerStyles}>
      <Box>
        {/*section title*/}
        <Flex
          alignItems={{ base: 'start', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          mb="20px"
          justifyContent={{ base: 'flex-start', md: 'space-between' }}
        >
          <Text sx={overviewSectionTitleStyles} mb={{ base: 1, md: 0 }}>
            {title}
          </Text>
          {canEdit && onEdit && (
            <AleaEditButton
              data-testid={editButtonTestId}
              onClick={onEdit}
              label={t('edit')}
              size="large"
            />
          )}
        </Flex>

        <Box>
          <Flex flexFlow="column" mb="25px">
            {/*name*/}
            <Box>
              <Text sx={overviewItemTitleStyles}>
                {nameLabel || t('name')}
              </Text>
              <Text sx={overviewItemValueStyles}>
                {name || '-'}
              </Text>
            </Box>
          </Flex>

          <Flex mb="25px">
            {/*street*/}
            <Box flex={1} mr="75px">
              <Text flex={1} sx={overviewItemTitleStyles}>
                {t('street')}
              </Text>
              <Text flex={1} sx={overviewItemValueStyles}>
                {address?.street_name || '-'}
              </Text>
            </Box>

            {/*houseNumber*/}
            <Box flex={1} mr="75px">
              <Text sx={overviewItemTitleStyles}>{t('houseNumber')}</Text>
              <Text fontSize="18px" fontWeight="400" color="#000000">
                {address?.house_number || '-'}
              </Text>
            </Box>
          </Flex>

          <Flex mb="25px">
            <Flex flex="1">
              {/*postalCode*/}
              <Box mr="34px">
                <Text sx={overviewItemTitleStyles}>{t('postalCode')}</Text>
                <Text sx={overviewItemValueStyles}>
                  {address?.postal_code || '-'}
                </Text>
              </Box>

              {/*city*/}
              <Box mr="34px">
                <Text sx={overviewItemTitleStyles}>{t('city')}</Text>
                <Text sx={overviewItemValueStyles}>
                  {address?.city || '-'}
                </Text>
              </Box>
            </Flex>

            <Flex flex="1">
              {/*country*/}
              <Box>
                <Text sx={overviewItemTitleStyles}>{t('country')}</Text>
                <Text sx={overviewItemValueStyles}>
                  {address?.country || '-'}
                </Text>
              </Box>
            </Flex>
          </Flex>

          <Flex>
            {/*website*/}
            <Box mb="10px">
              <Text sx={overviewItemTitleStyles}>{t('website')}</Text>
              <Text sx={overviewItemValueStyles}>
                {address?.website || '-'}
              </Text>
            </Box>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default AddressSection;

